import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sun, Moon } from 'lucide-react';
import { useTheme, ThemePreset, ThemeMode, themePresets } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

const presetNames: Record<ThemePreset, string> = {
  default: 'Lemon',
  ocean: 'Ocean',
  sunset: 'Sunset',
  forest: 'Forest',
  cyber: 'Cyber',
  rose: 'Rose',
};

const presetDescriptions: Record<ThemePreset, string> = {
  default: 'Classic purple with bold contrast',
  ocean: 'Deep blue with calm vibes',
  sunset: 'Warm orange tones',
  forest: 'Natural green palette',
  cyber: 'Electric cyan accents',
  rose: 'Elegant pink hues',
};

interface ThemePreviewProps {
  onThemeChange?: (preset: ThemePreset) => void;
}

export default function ThemePreview({ onThemeChange }: ThemePreviewProps) {
  const { theme, setPreset, setMode } = useTheme();
  const [previewMode, setPreviewMode] = React.useState<'light' | 'dark'>(theme.mode === 'system' ? 'dark' : theme.mode);

  // Update preview mode when theme mode changes
  React.useEffect(() => {
    if (theme.mode === 'light' || theme.mode === 'dark') {
      setPreviewMode(theme.mode);
    } else if (theme.mode === 'system') {
      // Determine system preference
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setPreviewMode(isDark ? 'dark' : 'light');
    }
  }, [theme.mode]);

  const handlePresetClick = (preset: ThemePreset) => {
    // Force update to ensure re-render
    setPreset(preset);
    onThemeChange?.(preset);
    
    // Force immediate re-render by updating a local state that depends on theme
    setPreviewMode(prev => prev);
  };

  return (
    <div className="space-y-6">
      {/* Mode Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Theme Preview</h3>
          <p className="text-sm text-muted-foreground">
            Select a color scheme and preview in light or dark mode
          </p>
        </div>
        <div className="flex items-center gap-2 p-1 rounded-lg bg-secondary">
          <Button
            variant={previewMode === 'light' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setPreviewMode('light');
              setMode('light');
            }}
            className="h-8"
          >
            <Sun className="h-4 w-4 mr-2" />
            Light
          </Button>
          <Button
            variant={previewMode === 'dark' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setPreviewMode('dark');
              setMode('dark');
            }}
            className="h-8"
          >
            <Moon className="h-4 w-4 mr-2" />
            Dark
          </Button>
        </div>
      </div>

      {/* Theme Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(Object.keys(themePresets) as ThemePreset[]).map((preset) => {
          const isActive = theme.preset === preset;
          const presetConfig = themePresets[preset];

          return (
            <Card
              key={preset}
              className={cn(
                'relative overflow-hidden cursor-pointer transition-all hover:shadow-lg',
                isActive && 'ring-2 ring-primary'
              )}
              onClick={() => handlePresetClick(preset)}
            >
              {/* Preview area with simulated theme colors */}
              <div
                className="h-32 relative"
                style={{
                  background: previewMode === 'dark'
                    ? `linear-gradient(135deg, hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 15%) 0%, hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 8%) 100%)`
                    : `linear-gradient(135deg, hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 98%) 0%, hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 92%) 100%)`
                }}
              >
                {/* Simulated UI elements */}
                <div className="absolute inset-4 space-y-2">
                  {/* Header bar */}
                  <div
                    className="h-6 rounded"
                    style={{
                      background: previewMode === 'dark'
                        ? `hsla(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 20%, 0.5)`
                        : `hsla(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 100%, 0.8)`
                    }}
                  />
                  
                  {/* Content cards */}
                  <div className="flex gap-2">
                    <div
                      className="flex-1 h-16 rounded"
                      style={{
                        background: `hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, ${previewMode === 'dark' ? '58%' : '58%'})`
                      }}
                    />
                    <div
                      className="flex-1 h-16 rounded"
                      style={{
                        background: previewMode === 'dark'
                          ? `hsla(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 25%, 0.6)`
                          : `hsla(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 95%, 0.8)`
                      }}
                    />
                  </div>
                </div>

                {/* Active indicator */}
                {isActive && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-primary rounded-full p-1">
                      <Check className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                )}
              </div>

              {/* Theme info */}
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{presetNames[preset]}</h4>
                    {isActive && (
                      <Badge variant="secondary" className="text-xs">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {presetDescriptions[preset]}
                  </p>
                  
                  {/* Color dots */}
                  <div className="flex gap-1.5 pt-2">
                    <div
                      className="w-4 h-4 rounded-full border border-border"
                      style={{
                        background: `hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, 58%)`
                      }}
                      title="Primary"
                    />
                    <div
                      className="w-4 h-4 rounded-full border border-border"
                      style={{
                        background: `hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, ${previewMode === 'dark' ? '15%' : '98%'})`
                      }}
                      title="Background"
                    />
                    <div
                      className="w-4 h-4 rounded-full border border-border"
                      style={{
                        background: `hsl(${presetConfig.primaryHue}, ${presetConfig.primarySat}%, ${previewMode === 'dark' ? '25%' : '92%'})`
                      }}
                      title="Card"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info note */}
      <div className="p-4 rounded-lg bg-muted/50 border border-border">
        <p className="text-sm text-muted-foreground">
          💡 <strong>Tip:</strong> Themes are applied globally across the entire app. 
          The preview shows how your UI will look with each color scheme. 
          Toggle between Light and Dark modes to see both variations.
        </p>
      </div>
    </div>
  );
}
